import { CaretRight } from "@phosphor-icons/react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import {
  Children,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ForwardedRef,
  forwardRef,
  isValidElement,
  type ReactNode,
} from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import Checkbox from "common/components/ui/Checkbox";
import { cn } from "common/helpers/utils";

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuSubTrigger = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      dropdownMenuItemVariants({ variant: "default" }),
      "data-[state=open]:bg-black-100",
      className,
    )}
    {...props}
  >
    {children}
    <CaretRight size={16} className="ml-auto size-4" />
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(dropdownMenuContentClassname, "ml-3", className)}
    {...props}
  />
));
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const dropdownMenuItemVariants = cva(
  "relative flex cursor-pointer select-none items-center gap-1.5 rounded-xl p-2 text-sm-medium outline-none transition-colors data-[disabled]:cursor-not-allowed data-[disabled]:text-black-400 [&>svg:first-child]:size-5",
  {
    variants: {
      variant: {
        default: "text-black-950 hover:bg-black-50 focus:bg-black-50",
        danger: "text-red-500 hover:bg-red-50 focus:bg-red-50",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

interface DropdownMenuItemProps
  extends ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>,
    VariantProps<typeof dropdownMenuItemVariants> {
  isSubItem?: boolean;
}

function DropdownMenuItemComponent(
  { className, variant, isSubItem = false, ...props }: DropdownMenuItemProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cn(
        dropdownMenuItemVariants({ variant }),
        className,
        isSubItem && "pl-6",
      )}
      {...props}
    />
  );
}

DropdownMenuItemComponent.displayName = DropdownMenuPrimitive.Item.displayName;
const DropdownMenuItem = forwardRef(DropdownMenuItemComponent);

const DropdownMenuLabel = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Label>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      "px-3.5 pb-1.5 pt-2.5 text-xs-medium text-black-500",
      className,
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn("my-1 h-px bg-black-200", className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

function DropdownMenuCheckbox({
  disabled = false,
  isChecked,
  onCheckedChange,
}: {
  disabled?: boolean;
  isChecked: boolean;
  onCheckedChange: () => void;
}) {
  return (
    <Checkbox
      disabled={disabled}
      checked={isChecked}
      onClick={(e) => e.stopPropagation()}
      onCheckedChange={onCheckedChange}
      size="sm"
    />
  );
}

function DropdownMenuCount({ children }: { children: string | number }) {
  return <span className="ml-auto text-xs text-black-500">{children}</span>;
}

const dropdownMenuListClassname =
  "my-1.5 mr-1 max-h-[216px] overflow-x-hidden overflow-y-auto pl-1.5 pr-0.5 scrollbar-thin scrollbar-thumb-black-200 flex flex-col";

function DropdownMenuList({ children }: { children: ReactNode }) {
  const isEmpty = Array.isArray(children) ? !children.length : !children;

  return (
    <div className={dropdownMenuListClassname}>
      {isEmpty ? (
        <p className="text-black-500 my-12 text-center">No results found</p>
      ) : (
        children
      )}
    </div>
  );
}
DropdownMenuList.displayName = "DropdownMenuList";

const dropdownMenuContentClassname =
  "z-popover overflow-hidden rounded-3xl bg-white shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2";

const DropdownMenuContent = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, children, ...props }, ref) => {
  Children.forEach(children, (child) => {
    if (
      isValidElement(child) &&
      child.type !== DropdownMenuList &&
      child.type !== MenuSearchInput &&
      child.type !== DropdownMenuSeparator &&
      child.type !== DropdownMenuHeader &&
      child.type !== DropdownMenuFooter
    ) {
      console.error(
        `Dropdown menu content children must be DropdownMenuList, DropdownMenuSeparator or DropdownMenuSearchInput, DropdownMenuHeader, DropdownMenuFooter,
        received "${child.type}"`,
      );
    }
  });

  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(dropdownMenuContentClassname, className)}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  );
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

function DropdownMenuFooter({ children }: { children: ReactNode }) {
  return <div className="border-t border-t-black-200 p-1.5">{children}</div>;
}
DropdownMenuFooter.displayName = "DropdownMenuFooter";

function DropdownMenuHeader({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx("border-b border-b-black-200 py-2 px-3", className)}>
      {children}
    </div>
  );
}
DropdownMenuHeader.displayName = "DropdownMenuHeader";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuCount,
  DropdownMenuCheckbox,
  DropdownMenuList,
  DropdownMenuFooter,
  DropdownMenuHeader,
  dropdownMenuItemVariants,
  dropdownMenuContentClassname,
  dropdownMenuListClassname,
};
